import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, ParamMap, Router} from '@angular/router';
import {defaultIfEmpty, map, Subscription, switchMap, take, tap, zip} from 'rxjs';
import {SpinnerService} from 'src/app/utils/services/spinner.service';
import {ItemAvailability, V3Chapter, V3ExerciseSet, V3Section} from '../../model/v3/cspa';
import {NativeServiceApiProvider} from "../../../services/native-api-provider.service";
import {CspaRestV3} from "../../services/v3/cspa-rest-v3";
import {LogsService} from "../../../utils/services/logs.service";
import {AuthorizationServiceProvider} from "../../../auth_profile/services/authorization-service.provider";
import {AuthorizedUserDetailsService} from "../../../auth_profile/services/api/authorization.api";

@Component({
    selector: 'exercise-details',
    templateUrl: './exercise-details.component.html',
    styleUrls: ['./exercise-details.component.scss'],
    standalone: false
})
export class ExerciseDetailsComponent implements OnInit, OnDestroy {
  exerciseSet: V3ExerciseSet
  chapter: V3Chapter
  sectionsMap = new Map<string, V3Section>()
  availabilitiesMap = new Map<string, ItemAvailability>()
  personId: number
  private updateSubscription: Subscription
  private refreshSubscription: Subscription
  private refreshDataSubscription: Subscription;
  private loadDataSubscription: Subscription;

  constructor(
    private route: ActivatedRoute,
    private provider: NativeServiceApiProvider,
    private router: Router,
    private spinner: SpinnerService,
    private logger: LogsService,
    private auth: AuthorizationServiceProvider,
    ) {}

  ngOnInit(): void {
    this.loadDataSubscription = this.loadData().subscribe()
    this.refreshSubscription = this.provider.books().pipe(
      take(1),
      map(api => api.subscribeForRefreshingEvents()),
      switchMap(taskProgressEmitter => taskProgressEmitter),
      switchMap(observable => this.spinner.trace(observable))
    ).subscribe()

    this.updateSubscription = this.provider.books().pipe(
      switchMap(api => api.listenForDataUpdates()),
      defaultIfEmpty("anything"),
    ).subscribe(_ =>
      this.refreshData()
    )
  }

  ngOnDestroy(): void {
    this.updateSubscription?.unsubscribe()
    this.refreshSubscription?.unsubscribe()
    this.loadDataSubscription?.unsubscribe()
    this.refreshDataSubscription?.unsubscribe()
  }

  loadData() {
    this.logger.log("Loading exercise details view data")
    return this.spinner.trace(
      zip(this.route.paramMap, this.auth.getAuthDetailsService(), this.provider.cspaV3()).pipe(
        switchMap(([params, authDetailsApi, api]) => this.loadPersonId(authDetailsApi).pipe(
          switchMap(_ => this.findExerciseSet(api, params)),
          switchMap(_ => this.findChapter(api, params)),
          switchMap(_ => this.findChapterAvailabilities(api))
          )
        )
      )
    )
  }

  private findExerciseSet(api: CspaRestV3, params: ParamMap) {
    return api.listExerciseSets().pipe(
      map(exerciseSets =>
        exerciseSets.find(exerciseSet =>
          exerciseSet.path === params.get("exerciseSet")
        )
      ),
      tap<V3ExerciseSet>(exerciseSet =>
        this.exerciseSet = exerciseSet
      )
    )
  }

  private findChapter(api: CspaRestV3, params: ParamMap) {
    return api.getChapters(`${this.exerciseSet.path}_`, 3).pipe(
      map<V3Chapter[], V3Chapter>(chapters =>
        chapters.find( chapter => chapter.path === params.get("chapter"))
      ),
      tap<V3Chapter>(chapter => {
        this.chapter = chapter
      })
    )
  }

  private findChapterAvailabilities(api: CspaRestV3) {
    return api.listPersonAvailabilities(this.personId, `${this.chapter.path}_`, 2).pipe(
      tap(availabilities => this.mapAvailabilities(availabilities)),
      tap( _ =>
        this.mapSections(
          this.getSections().filter(section =>
            this.availabilitiesMap.has(section.path)
          )
        )
      )
    )
  }

  refreshData() {
    this.refreshDataSubscription = this.spinner.trace(this.provider.books().pipe(
      switchMap(api => api.refreshData(0))
    )).subscribe()
  }

  getExerciseSet(){
    return this.exerciseSet?.name
  }

  getChapter() {
    return this.chapter?.shortName
  }

  getAccent() {
    return `accent stg-${this.chapter?.path.split('_')[1]}`
  }

  getSections() {
    return this.chapter?.nested ?? []
  }

  mapSections(sections: V3Section[]) {
    sections.forEach(section =>
      this.sectionsMap.set(section.path, section))
  }

  mapAvailabilities(availabilities: ItemAvailability[]) {
    availabilities.forEach(availability =>
      this.availabilitiesMap.set(availability.path, availability))
  }

  getAvailabilities(section: V3Section) {
    return new Map([...this.availabilitiesMap.entries()].filter(av =>
      av[0].includes(section.path)))
  }

  getSectionAvailability(section: V3Section): boolean {
    const sectionAvailability = this.getAvailabilities(section).values().next().value;
    return sectionAvailability?.isAssigned || false
  }

  private loadPersonId(authDetailsApi: AuthorizedUserDetailsService) {
    return authDetailsApi.getSelfPersonId().pipe(
      tap(personId => this.personId = personId)
    )
  }

  goToExerciseSetsWithFilter() {
    this.router.navigate(
      ['../../'],
      {
        relativeTo: this.route,
        queryParams: { language: `${this.exerciseSet.path}`},
        queryParamsHandling: 'merge'
      }
    ).then()
  }
}
